import React, { useContext, useEffect, useState } from 'react';
import {
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    FormProps,
    Input,
    Row,
    Select,
    Table,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { RangePickerProps } from 'antd/lib/date-picker';
import { topTitleContext } from '../../contexts/TopTitle';
import * as S from './style';
import { ImportReportsColumn } from './columns';
import {
    TypeOfImportRequest,
    useImportRequest,
} from '../../store/ImportRequest';
import { authContext } from '../../contexts/Auth';
import { menuContext } from '../../contexts/Menu';
import { ErrorModal } from './_components/ErrorModal';

interface FieldType {
    email?: string;
    id?: number;
    startDate?: string;
    endDate?: string;
    type?: 'PRODUCT_GLOBAL' | 'PRODUCT';
}

const optionTypeImport = [
    { label: 'Produto Global', value: 'PRODUCT_GLOBAL' },
    { label: 'Produto', value: 'PRODUCT' },
];

const ExportsReports = () => {
    const { titleValue } = useContext(topTitleContext);
    const { isAdmin } = useContext(authContext);
    const { isCollapsed } = useContext(menuContext);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [selectedImportId, setSelectedImportId] = useState<number | null>(
        null,
    );
    const [form] = Form.useForm();

    const [meta, setMeta] = useState<TypeOfImportRequest>({
        page: 1,
        perPage: 10,
    });

    const [startDate, setStartDate] = useState<Moment | null>();
    const [endDate, setEndtDate] = useState<Moment | null>();

    const { data: imports, isLoading: isLoadingImports } =
        useImportRequest(meta);

    const width = isCollapsed
        ? window.innerWidth - 249 - 57
        : window.innerWidth - 79 - 57;

    const onSearch: FormProps<FieldType>['onFinish'] = ({
        email,
        endDate,
        startDate,
        id,
        type,
    }) => {
        setMeta({
            perPage: 10,
            page: 1,
            email,
            id: id ? Number(id) : undefined,
            type,
            startDate: startDate
                ? moment(startDate).format('YYYY-MM-DD')
                : undefined,
            endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined,
        });
    };

    const disabledDate: RangePickerProps['disabledDate'] = (current) => {
        // Can not select days before today and today
        return current && current > moment().endOf('day');
    };

    const openErrorModal = (importId: number) => {
        setShowErrorModal(true);
        setSelectedImportId(importId);
    };

    const columns = ImportReportsColumn(openErrorModal);

    const onReset = () => {
        setMeta({
            page: 1,
            perPage: 10,
        });
        setStartDate(undefined);
        setEndtDate(undefined);
    };

    useEffect(() => {
        titleValue('Importações');
    }, []);

    return (
        <Row style={{ padding: '0px 20px' }}>
            <Col span={24}>
                <Form form={form} layout="vertical" onFinish={onSearch}>
                    <Row gutter={16}>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item<FieldType>
                                label="ID de Importação"
                                name="id"
                                rules={[
                                    {
                                        type: 'number',
                                        message:
                                            'Por favor, informe um ID válido',
                                        transform: (value) =>
                                            value ? Number(value) : undefined,
                                    },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item<FieldType>
                                label="Data Inícial"
                                name="startDate"
                                rules={[
                                    {
                                        required: !!endDate,
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    disabledDate={disabledDate}
                                    onChange={(e) => setStartDate(e)}
                                />
                            </Form.Item>
                        </Col>

                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item<FieldType>
                                label="Data Final"
                                name="endDate"
                                rules={[
                                    {
                                        required: !!startDate,
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    disabledDate={disabledDate}
                                    onChange={(e) => setEndtDate(e)}
                                />
                            </Form.Item>
                        </Col>

                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 8 }}
                            md={{ span: 6 }}
                        >
                            <Form.Item<FieldType>
                                label="E-mail do Usuário"
                                name="email"
                                rules={[
                                    {
                                        type: 'email',
                                        message:
                                            'Por favor, informe um email válido',
                                    },
                                ]}
                            >
                                <Input
                                    onChange={(e) => {
                                        form.setFieldValue(
                                            'email',
                                            e.currentTarget.value.trim(),
                                        );
                                        form.validateFields(['email']);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        {!isAdmin && (
                            <Col
                                className="gutter-row"
                                xs={{ span: 24 }}
                                sm={{ span: 8 }}
                                md={{ span: 6 }}
                            >
                                <Form.Item<FieldType> label="Tipo" name="type">
                                    <Select options={optionTypeImport} />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    <Row style={{ paddingTop: '2rem' }} gutter={[8, 16]}>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 6 }}
                            xl={{ span: 5 }}
                            xxl={{ span: 4 }}
                        >
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                icon={<SearchOutlined />}
                                type="primary"
                                style={{ width: '100%' }}
                            >
                                Pesquisar
                            </Button>
                        </Col>
                        <Col
                            className="gutter-row"
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 6 }}
                            xl={{ span: 5 }}
                            xxl={{ span: 4 }}
                        >
                            <Button
                                danger
                                onClick={onReset}
                                htmlType="reset"
                                style={{ width: '100%' }}
                            >
                                Limpar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
            <Divider />
            <Col span={24}>
                <Row justify="space-between" style={{ margin: 0 }}>
                    <Col>
                        <S.TitleResult>
                            A pesquisa retornou {imports?.meta.total}
                            &nbsp;resultado(s)
                        </S.TitleResult>
                    </Col>
                </Row>
            </Col>
            <Col span={24} style={{ marginTop: 32 }}>
                <Table
                    dataSource={imports?.data || []}
                    columns={columns}
                    rowKey="id"
                    loading={isLoadingImports}
                    scroll={{
                        x: 'max-content',
                    }}
                    pagination={{
                        total: imports?.meta.total,
                        current: imports?.meta.current_page,
                        onChange: (page, pgZise) => {
                            setMeta({
                                ...meta,
                                page,
                                perPage: pgZise,
                            });
                        },
                    }}
                />
            </Col>
            <ErrorModal
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                importId={selectedImportId}
            />
        </Row>
    );
};

export default ExportsReports;
